import {
  RP_LIST_CAPS,
  rpSectionLibraryEntry,
  type RpButton,
  type RpSection,
  type RpSectionStyle,
} from '@mlm/shared';
import { RpButtonsField, RpFieldControl, RpFieldRow } from './fields';
import { RP_FIELD_SPECS } from './field-specs';
import { RpStylePanel } from './style-panel';

/**
 * The editor for the selected section: its content fields, then its style.
 *
 * Content controls come from the declarative spec table rather than a bespoke
 * component per type, so all ten sections get the same labels, hints and reset
 * behavior and adding a type is a data edit.
 */
export function RpSectionInspector({
  section,
  onChange,
}: {
  section: RpSection;
  onChange: (next: RpSection) => void;
}) {
  const entry = rpSectionLibraryEntry(section.type);
  const specs = RP_FIELD_SPECS[section.type] ?? [];
  const config = section.config as Record<string, unknown>;

  const setConfig = (key: string, value: unknown) =>
    onChange({ ...section, config: { ...config, [key]: value } } as RpSection);
  const setStyle = (style: RpSectionStyle) => onChange({ ...section, style });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold">{entry?.label ?? section.type}</h3>
        {entry?.note ? (
          <p className="mt-0.5 text-[11px] text-muted-foreground">{entry.note}</p>
        ) : null}
      </div>

      <div className="space-y-3">
        {specs.map((spec) => (
          <RpFieldControl key={spec.key} spec={spec} config={config} onChange={setConfig} />
        ))}

        {/* The one list the spec table can't express: an array of typed buttons
            rather than an array of flat strings. */}
        {section.type === 'buttonGroup' ? (
          <RpFieldRow label="Buttons">
            <RpButtonsField
              value={Array.isArray(config.buttons) ? (config.buttons as RpButton[]) : []}
              max={RP_LIST_CAPS.buttonGroupButtons}
              onChange={(buttons) => setConfig('buttons', buttons)}
            />
          </RpFieldRow>
        ) : null}

        {specs.length === 0 && section.type !== 'buttonGroup' ? (
          <p className="text-xs text-muted-foreground">
            This section has no content options yet — style it below.
          </p>
        ) : null}
      </div>

      <details className="rounded-md border" open>
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold">
          Style
        </summary>
        <div className="border-t p-3">
          <RpStylePanel style={section.style ?? {}} onChange={setStyle} />
        </div>
      </details>
    </div>
  );
}
